import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Teams from './pages/Teams';
import Players from './pages/Players';
import Tournaments from './pages/Tournaments';
import Matches from './pages/Matches';
import Scorecards from './pages/Scorecards';
import Venues from './pages/Venues';
import Umpires from './pages/Umpires';
import PointsTable from './pages/PointsTable';
import LiveScore from './pages/LiveScore';
import PublicLiveScore from './pages/PublicLiveScore';
import OrganizationSelector from './components/OrganizationSelector';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import AdminUsers from './pages/admin/AdminUsers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ImportExport from './pages/ImportExport';

// Protected route for superadmin only
const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Protected route for organization selection
const OrgSelectionRoute = ({ children }) => {
  const { isAuthenticated, isSuperAdmin, needsOrgSelection, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (!needsOrgSelection) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/public/live" element={<PublicLiveScore />} />
            <Route path="/live" element={
              <div className="min-h-screen bg-gray-50">
                <Navbar />
                <main className="container mx-auto px-4 py-8">
                  <LiveScore />
                </main>
              </div>
            } />

            {/* Organization selection route */}
            <Route path="/select-organization" element={
              <OrgSelectionRoute>
                <OrganizationSelector />
              </OrgSelectionRoute>
            } />

            {/* Super Admin routes */}
            <Route path="/admin/*" element={
              <SuperAdminRoute>
                <div className="min-h-screen bg-gray-50">
                  <Navbar />
                  <main className="container mx-auto px-4 py-8">
                    <Routes>
                      <Route path="/" element={<AdminDashboard />} />
                      <Route path="/organizations" element={<AdminOrganizations />} />
                      <Route path="/users" element={<AdminUsers />} />
                    </Routes>
                  </main>
                  <Toaster position="top-right" />
                </div>
              </SuperAdminRoute>
            } />

            {/* Protected routes */}
            <Route path="/dashboard/*" element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gray-50">
                  <Navbar />
                  <main className="container mx-auto px-4 py-8">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/teams" element={<Teams />} />
                      <Route path="/players" element={<Players />} />
                      <Route path="/tournaments" element={<Tournaments />} />
                      <Route path="/matches" element={<Matches />} />
                      <Route path="/scorecards" element={<Scorecards />} />
                      <Route path="/venues" element={<Venues />} />
                      <Route path="/umpires" element={<Umpires />} />
                      <Route path="/points-table" element={<PointsTable />} />
                      <Route path="/import-export" element={<ImportExport />} />
                    </Routes>
                  </main>
                  <Toaster position="top-right" />
                </div>
              </ProtectedRoute>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
